import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { FileClock, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

const billPaymentForm = type({
	id: "number.integer > 0",
	paid_on: "string",
});

export function CashBillPaymentModal({ date, onClose }: { date: string; onClose: () => void }) {
	const queryClient = useQueryClient();
	const bills = useQuery(orpc.financeiro.bills.open.queryOptions());
	const openBills = bills.data;
	const { mutate, isPending } = useMutation(
		orpc.financeiro.bills.pay.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Conta paga em espécie");
				onClose();
			},
			onError: () => toast.error("Não foi possível registrar o pagamento"),
		}),
	);
	const {
		control,
		register,
		handleSubmit,
		trigger,
		formState: { isValid },
	} = useForm<typeof billPaymentForm.infer>({
		resolver: arktypeResolver(billPaymentForm),
		mode: "onChange",
		defaultValues: {
			id: 0,
			paid_on: date,
		},
	});
	const close = () => {
		if (!isPending) {
			onClose();
		}
	};

	useEffect(() => {
		void trigger();
	}, [trigger]);

	return (
		<OverlayShell
			label="Pagar conta em espécie"
			onClose={close}
			className="items-center justify-center p-4"
		>
			<form
				aria-busy={isPending}
				onSubmit={handleSubmit((value) => mutate({ ...value, payment_method: "cash" }))}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">Pagar conta em espécie</h2>
					<p className="mt-1 text-xs text-muted-foreground">O pagamento reduz o saldo do caixa.</p>
				</div>

				{bills.isLoading && (
					<div className="flex h-32 items-center justify-center text-muted-foreground">
						<Loader2 className="animate-spin" />
					</div>
				)}

				{bills.isError && <EmptyState icon={FileClock} title="Não foi possível buscar as contas" />}

				{!bills.isLoading && !bills.isError && openBills?.length === 0 && (
					<EmptyState icon={FileClock} title="Nenhuma conta em aberto" />
				)}

				{!bills.isLoading && !bills.isError && openBills !== undefined && openBills.length > 0 && (
					<>
						<label className="flex flex-col gap-1.5 text-sm font-medium">
							Conta
							<select
								{...register("id", { valueAsNumber: true })}
								disabled={isPending}
								autoFocus
								className="control-field h-11 md:h-10"
							>
								<option value={0}>Selecione uma conta</option>
								{openBills.map((bill) => (
									<option key={bill.id} value={bill.id}>
										{bill.description}, vencimento em {format.date(bill.due_on)} (
										{FinanceiroLabels.money(bill.amount_cents)})
									</option>
								))}
							</select>
						</label>

						<label className="flex flex-col gap-1.5 text-sm font-medium">
							Data do pagamento
							<Controller
								control={control}
								name="paid_on"
								render={({ field }) => (
									<DatePicker
										value={field.value}
										onChange={(value) => value !== null && field.onChange(value)}
										disabled={isPending}
										containerClassName="w-full"
										className="w-full justify-between"
									/>
								)}
							/>
						</label>
					</>
				)}

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" disabled={isPending} onClick={close}>
						Fechar
					</Button>
					{openBills !== undefined && openBills.length > 0 && (
						<Button type="submit" disabled={!isValid || isPending}>
							{isPending && <Loader2 className="animate-spin" />}
							{isPending && "Registrando"}
							{!isPending && "Registrar pagamento"}
						</Button>
					)}
				</div>
			</form>
		</OverlayShell>
	);
}
